import { computed, ref, shallowRef } from 'vue';
import type { CalendarRestriction } from '@/types/SearchBar';
import type { DurationMode } from '@/utils/filters/durationMode';
import { $t, $tHtml } from '@/composables/translations/useTranslate';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import { MAX_PRODUCT_DURATION } from '@/utils/filters/durationPlan';
import { daysBetweenKeys, departureDaySet, minStay } from '@/utils/calendar/hotelAvailability';

export type SelectionPhase = 'start' | 'loading' | 'end';
export type DayArrow = 'arrival' | 'departure' | null;

export interface RestrictedRangeSelectionOptions {
  restriction: () => CalendarRestriction | undefined;
  mode: () => DurationMode;
  /** Last day an arrival may fall on; departures may run past it. */
  maxDate: () => Date | null;
  /** Puts a single start day into the picker (restart on an earlier arrival). */
  setPendingModel: (start: Date) => void;
  /** Shows the last confirmed range again (cancelled or dropped selection). */
  restoreConfirmed: () => void;
  /** End day of the confirmed range; it always shows the departure arrow. */
  confirmedEnd: () => string | null;
  /** `html` is sanitized and ready for `v-html`. */
  showHint: (html: string, anchor: string) => void;
}

/**
 * Selection rules of the hotel-restricted range picker (VAM-65, SPEC §6.1/§6.2).
 *
 * Exact: arrival on an arrival day, then request C decides the departure days.
 * Preset: the window starts on an arrival day and is at least that day's
 * minimum stay long (request C, falling back to the hotel's shortest stay),
 * with no departure days shown. The library only sees `disabled-dates`;
 * everything it reports as invalid comes back here as a restart, a hint or nothing.
 */
export function useRestrictedRangeSelection(options: RestrictedRangeSelectionOptions) {
  const phase = ref<SelectionPhase>('start');
  const pendingStart = ref<string | null>(null);
  const departures = shallowRef<Set<string> | null>(null);
  const stayMin = ref<number | null>(null);
  let token = 0;

  const restriction = computed(() => options.restriction());
  const active = computed(() => restriction.value?.arrivalStatus === 'ready');
  const loadingArrivals = computed(() => restriction.value?.arrivalStatus === 'loading');
  const isExact = computed(() => options.mode() === 'exact');
  const maxKey = computed(() => {
    const max = options.maxDate();
    return max ? formatDateLocal(max) : null;
  });

  const firstDeparture = computed(() => {
    if (!departures.value || departures.value.size === 0) return null;
    return [...departures.value].sort()[0];
  });

  /** Nights of the minimum-stay band after the pending start, or `null` for none. */
  const bandLength = computed<number | null>(() => {
    if (phase.value !== 'end' || !pendingStart.value) return null;
    const length = isExact.value
      ? departures.value
        ? stayMin.value
        : null
      : (stayMin.value ?? restriction.value?.minOffered ?? 1);
    return length !== null && length >= 2 ? length : null;
  });

  function isArrival(key: string): boolean {
    if (!restriction.value?.arrivalDays.has(key)) return false;
    return !maxKey.value || key <= maxKey.value;
  }

  function nightsFromStart(key: string): number {
    return pendingStart.value ? daysBetweenKeys(pendingStart.value, key) : 0;
  }

  function inBand(key: string): boolean {
    if (bandLength.value === null) return false;
    const nights = nightsFromStart(key);
    return nights >= 1 && nights < bandLength.value;
  }

  function isValidEnd(key: string): boolean {
    const nights = nightsFromStart(key);
    if (isExact.value) {
      return departures.value
        ? departures.value.has(key)
        : nights >= 1 && nights <= MAX_PRODUCT_DURATION;
    }
    return nights >= Math.max(1, stayMin.value ?? restriction.value?.minOffered ?? 1);
  }

  function isRestartTarget(key: string): boolean {
    return (
      phase.value !== 'start' && !!pendingStart.value && key < pendingStart.value && isArrival(key)
    );
  }

  function isDeparture(key: string): boolean {
    return phase.value === 'end' && isExact.value && !!departures.value?.has(key);
  }

  /** `disabled-dates` for the library: O(1) per call, it runs several times per cell. */
  function isDateDisabled(date: Date): boolean {
    if (loadingArrivals.value) return true;
    if (!active.value) return false;

    const key = formatDateLocal(date);
    if (phase.value === 'start') return !isArrival(key);
    if (key === pendingStart.value) return false;
    if (phase.value === 'loading') return true;
    return !isValidEnd(key);
  }

  function arrowFor(key: string): DayArrow {
    if (!active.value) return null;
    if (isDeparture(key)) return 'departure';
    if (phase.value === 'start') {
      if (key === options.confirmedEnd()) return 'departure';
      return isArrival(key) ? 'arrival' : null;
    }
    if (key === pendingStart.value || isRestartTarget(key)) return 'arrival';
    return null;
  }

  function dayClass(date: Date): string {
    if (!active.value) return '';
    const key = formatDateLocal(date);
    const classes: string[] = [];

    const arrow = arrowFor(key);
    if (arrow === 'arrival') classes.push('is-arrival');
    if (arrow === 'departure') classes.push('is-departure');
    if (isRestartTarget(key)) classes.push('restart-target');
    if (phase.value === 'end' && key === firstDeparture.value) classes.push('first-departure');
    if (inBand(key)) {
      classes.push('min-band');
      if (nightsFromStart(key) === (bandLength.value ?? 0) - 1) classes.push('min-band-end');
    }
    return classes.join(' ');
  }

  function minStayText(nights: number): string {
    return $t('ibe.datepicker.min-stay', 'Minimum stay: {n} nights.', { n: nights });
  }

  function minStayHtml(nights: number): string {
    return $tHtml('ibe.datepicker.min-stay', 'Minimum stay: {n} nights.', { n: nights });
  }

  /** Screen-reader suffix for a day cell, or `null` when the day has no role. */
  function ariaLabelFor(key: string): string | null {
    if (!active.value) return null;
    if (inBand(key) && bandLength.value !== null) return minStayText(bandLength.value);
    const arrow = arrowFor(key);
    if (arrow === 'arrival') return $t('ibe.datepicker.aria-arrival', 'Arrival possible');
    if (arrow === 'departure') return $t('ibe.datepicker.aria-departure', 'Departure possible');
    return null;
  }

  function reset(): void {
    token++;
    phase.value = 'start';
    pendingStart.value = null;
    departures.value = null;
    stayMin.value = null;
  }

  function cancelPending(): void {
    if (!pendingStart.value) return;
    reset();
    options.restoreConfirmed();
  }

  function begin(key: string): void {
    reset();
    pendingStart.value = key;
    phase.value = 'loading';
    const current = token;
    const source = restriction.value;
    if (!source) return;

    source.durationsFor(key).then(
      (durations) => {
        if (current !== token) return;
        const nights = durations.filter((value) => value >= 1);
        if (nights.length === 0) {
          source.markNotArrival(key);
          cancelPending();
          options.showHint(
            $tHtml('ibe.datepicker.date-unavailable', 'This date is no longer available.'),
            key
          );
          return;
        }
        departures.value = isExact.value ? departureDaySet(key, nights) : null;
        stayMin.value = minStay(nights);
        phase.value = 'end';
        if (stayMin.value !== null && stayMin.value >= 2) {
          options.showHint(minStayHtml(stayMin.value), key);
        }
      },
      () => {
        if (current !== token) return;
        departures.value = null;
        stayMin.value = null;
        phase.value = 'end';
      }
    );
  }

  function handleRangeStart(date: Date): void {
    if (!active.value) return;
    begin(formatDateLocal(date));
  }

  function handleInvalidDate(date: Date): void {
    if (!active.value) return;
    const key = formatDateLocal(date);

    if (phase.value === 'start') {
      options.showHint(
        $tHtml(
          'ibe.datepicker.hint-arrival-days',
          'Days marked with an arrow are possible arrival days.'
        ),
        key
      );
      return;
    }
    if (isRestartTarget(key)) {
      options.setPendingModel(date);
      begin(key);
      return;
    }
    if (phase.value === 'loading') return;
    if (inBand(key) && bandLength.value !== null) {
      options.showHint(minStayHtml(bandLength.value), key);
      return;
    }
    if (isExact.value && departures.value) {
      options.showHint(
        $tHtml(
          'ibe.datepicker.hint-departure-days',
          'Days marked with an arrow are possible departure days.'
        ),
        key
      );
    }
  }

  /**
   * The library maps its day cells only when these props change identity, not
   * when state read inside them changes, so every state change hands it new closures.
   */
  const stateVersion = computed(() => [
    restriction.value,
    phase.value,
    pendingStart.value,
    departures.value,
    stayMin.value,
    isExact.value,
    maxKey.value,
    options.confirmedEnd(),
  ]);
  const disabledDates = computed(() => {
    void stateVersion.value;
    return (date: Date) => isDateDisabled(date);
  });
  const dayClassFn = computed(() => {
    void stateVersion.value;
    return (date: Date) => dayClass(date);
  });

  return {
    active,
    loadingArrivals,
    phase,
    pendingStart,
    disabledDates,
    dayClass: dayClassFn,
    arrowFor,
    ariaLabelFor,
    handleRangeStart,
    handleInvalidDate,
    cancelPending,
    reset,
  };
}
